import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiHeaders, getToken } from '../api';
import { resetUnreadGlobal } from '../components/Notifications';

const API_BASE = 'https://bot.nt55.ru';
const WS_URL = 'wss://bot.nt55.ru/ws/livechat';
const STORAGE_KEY = 'maxbot_unread_chats';

interface ChatRow {
  chat_id: number;
  type: string;
  title: string | null;
  status: string;
  account_id?: string | null;
  last_message_at?: string | null;
}

interface Message {
  body?: { text?: string; mid?: string; attachments?: any };
  sender?: { user_id: number; first_name?: string; is_bot: boolean };
  timestamp?: number;
}

interface Template {
  id: number;
  title: string;
  text: string;
  sort_order: number;
}

const loadCountsFromStorage = (): Record<number, number> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr: number[] = JSON.parse(raw);
      const counts: Record<number, number> = {};
      arr.forEach(id => { counts[id] = 1; });
      return counts;
    }
  } catch (e) {}
  return {};
};

const saveCountsToStorage = (counts: Record<number, number>) => {
  const ids = Object.entries(counts)
    .filter(([_, count]) => count > 0)
    .map(([id]) => Number(id));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

const isSameDay = (a: number, b: number) => {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() &&
         da.getMonth() === db.getMonth() &&
         da.getDate() === db.getDate();
};

const formatDayLabel = (ts: number) => {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(ts, today.getTime())) return 'Сегодня';
  if (isSameDay(ts, yesterday.getTime())) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
};

const LiveChat: React.FC = () => {
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<number, number>>(loadCountsFromStorage());
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [editAccountValue, setEditAccountValue] = useState('');

  const [templates, setTemplates] = useState<Template[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [operatorMids, setOperatorMids] = useState<Set<string>>(new Set());
  const [editingMid, setEditingMid] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeChatIdRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { saveCountsToStorage(unreadCounts); }, [unreadCounts]);
  useEffect(() => { activeChatIdRef.current = activeChatId; }, [activeChatId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [inputText]);

  const fetchUnreadCounts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/unread-counts`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        const counts: Record<number, number> = data.unread_counts || {};
        const newCounts: Record<number, number> = {};
        Object.entries(counts).forEach(([chatId, count]) => {
          newCounts[Number(chatId)] = count as number;
        });
        setUnreadCounts(prev => ({ ...prev, ...newCounts }));
      }
    } catch (e) { console.error(e); }
  };

  const loadChats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/chats`, { headers: apiHeaders() });
      if (res.ok) {
        const data: ChatRow[] = await res.json();
        setChats(data);
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (chatId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/chats/${chatId}/messages?limit=30`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.filter((m: any) => m != null).reverse());
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/templates`, { headers: apiHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    loadChats();
    loadTemplates();
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const ws = new WebSocket(`${WS_URL}?token=${token}`);
    ws.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }

      if (data.type === 'init_unread') {
        const counts: Record<number, number> = data.unread_counts || {};
        const newCounts: Record<number, number> = {};
        Object.entries(counts).forEach(([chatId, count]) => {
          newCounts[Number(chatId)] = count as number;
        });
        setUnreadCounts(prev => ({ ...prev, ...newCounts }));
      }
      else if (data.type === 'new_chat') loadChats();
      else if (data.type === 'chat_closed') {
        if (activeChatIdRef.current === data.chat_id) { setActiveChatId(null); setMessages([]); }
        setUnreadCounts(prev => { const updated = { ...prev }; delete updated[data.chat_id]; return updated; });
        loadChats();
      }
      else if (data.type === 'message') {
        const msg = data.message;
        if (!msg) return;
        const msgTs = msg.timestamp || Date.now();
        // Обновляем last_message_at локально
        setChats(prev => prev.map(c => c.chat_id === data.chat_id
          ? { ...c, last_message_at: new Date(msgTs).toISOString() }
          : c
        ));
        if (activeChatIdRef.current === data.chat_id) {
          setMessages(prev => [...prev, msg]);
        } else if (msg.sender && !msg.sender.is_bot) {
          setUnreadCounts(prev => ({ ...prev, [data.chat_id]: (prev[data.chat_id] || 0) + 1 }));
        }
      }
      else if (data.type === 'message_sent' && (data.body?.text || data.body?.attachments?.length)) {
        const msgTs = data.timestamp || Date.now();
        setChats(prev => prev.map(c => c.chat_id === data.chat_id
          ? { ...c, last_message_at: new Date(msgTs).toISOString() }
          : c
        ));
        if (data.chat_id === activeChatIdRef.current) {
          setMessages(prev => [...prev, {
            body: { text: data.body.text, attachments: data.body.attachments, mid: data.body.mid },
            sender: { is_bot: true, first_name: 'Оператор', user_id: 0 },
            timestamp: msgTs,
          }]);
          if (data.body.mid) {
            setOperatorMids(prev => new Set(prev).add(data.body.mid));
          }
        }
      }
      else if (data.type === 'message_edited') {
        setMessages(prev => prev.map(m => {
          if (m.body?.mid === data.mid) {
            return { ...m, body: { ...m.body, text: data.text } };
          }
          return m;
        }));
      }
      else if (data.type === 'message_deleted') {
        setMessages(prev => prev.filter(m => m.body?.mid !== data.mid));
      }
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [loadChats]);

  const openChat = (chatId: number) => {
    resetUnreadGlobal(chatId);
    setActiveChatId(chatId);
    setMessages([]);
    setEditingMid(null);
    setInputText('');
    loadMessages(chatId);
    setUnreadCounts(prev => { const updated = { ...prev }; delete updated[chatId]; return updated; });
  };

  const sendMessage = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !activeChatId) return;

    if (editingMid) {
      if (!inputText.trim()) return;
      wsRef.current.send(JSON.stringify({
        action: 'edit_message',
        chat_id: activeChatId,
        mid: editingMid,
        text: inputText,
      }));
      setEditingMid(null);
      setInputText('');
      return;
    }

    if (!inputText.trim() && !imageUrl.trim()) return;
    wsRef.current.send(JSON.stringify({
      text: inputText,
      chat_id: activeChatId,
      image_url: imageUrl.trim() || undefined,
    }));
    setInputText('');
    setImageUrl('');
    setShowImageInput(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startEdit = (mid: string, text: string) => {
    setEditingMid(mid);
    setInputText(text || '');
  };

  const cancelEdit = () => {
    setEditingMid(null);
    setInputText('');
  };

  const deleteMessage = (mid: string) => {
    if (!activeChatId) return;
    if (!window.confirm('Удалить сообщение?')) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      action: 'delete_message',
      chat_id: activeChatId,
      mid,
    }));
  };

  const insertTemplate = (text: string) => {
    setInputText(prev => prev ? `${prev}\n${text}` : text);
    setShowTemplates(false);
    textareaRef.current?.focus();
  };

  const handleCloseChat = () => {
    if (!activeChatId) return;
    if (!window.confirm('Завершить диалог?')) return;
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: 'close_chat', chat_id: activeChatId }));
      } else {
        setActiveChatId(null);
        setMessages([]);
        loadChats();
      }
    } catch (e) {
      setActiveChatId(null);
      setMessages([]);
      loadChats();
    }
  };

  const handleRefresh = () => {
    fetchUnreadCounts();
    loadChats();
    loadTemplates();
  };

  const startEditAccount = (chatId: number, currentValue: string | null) => {
    setEditingAccountId(chatId);
    setEditAccountValue(currentValue || '');
  };

  const saveAccount = async (chatId: number) => {
    const value = editAccountValue.trim();
    try {
      await fetch(`${API_BASE}/api/livechat/chats/${chatId}/account`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...apiHeaders() },
        body: JSON.stringify({ account_id: value }),
      });
      setChats(prev => prev.map(c => c.chat_id === chatId ? { ...c, account_id: value || null } : c));
    } catch (e) { console.error(e); }
    setEditingAccountId(null);
  };

  const formatTime = (ts?: number) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Вариант B: сортировка по времени последнего сообщения (новые выше)
  const sortedChats = [...chats]
    .filter(c => c.status === 'active')
    .sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return b.chat_id - a.chat_id;
    });

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: 32, fontFamily: '"Inter", "Roboto", sans-serif' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto', display: 'flex', gap: 24, height: 'calc(100vh - 64px)' }}>
        {/* Левая панель — список чатов */}
        <div style={{ width: 280, background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>Чаты</h3>
            <button onClick={handleRefresh} style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>🔄</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {sortedChats.map(c => {
              const unread = unreadCounts[c.chat_id] || 0;
              const isActive = activeChatId === c.chat_id;
              return (
                <div key={c.chat_id} onClick={() => openChat(c.chat_id)}
                  style={{
                    padding: 10, marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                    background: isActive ? '#e6f7ff' : unread > 0 ? '#fff7e6' : 'white',
                    border: isActive ? '1px solid #91d5ff' : '1px solid transparent',
                    transition: 'background 0.15s',
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 14 }}>{c.title || c.chat_id}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#52c41a' : '#ccc', display: 'inline-block' }} />
                      {unread > 0 && <span style={{ background: '#ff4d4f', color: 'white', borderRadius: '50%', padding: '1px 5px', fontSize: 11 }}>{unread}</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {c.type === 'dialog' ? 'Диалог' : c.type}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
                    {editingAccountId === c.chat_id ? (
                      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                        <input
                          value={editAccountValue}
                          onChange={(e) => setEditAccountValue(e.target.value)}
                          placeholder="Л/с"
                          maxLength={10}
                          style={{ width: 80, padding: '2px 4px', borderRadius: 4, border: '1px solid #d9d9d9' }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveAccount(c.chat_id); }}
                        />
                        <button onClick={() => saveAccount(c.chat_id)} style={{ padding: '2px 6px', fontSize: 12, background: '#1677ff', color: 'white', border: 'none', borderRadius: 4 }}>✓</button>
                        <button onClick={(e) => { e.stopPropagation(); setEditingAccountId(null); }} style={{ padding: '2px 6px', fontSize: 12, background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 4 }}>✕</button>
                      </div>
                    ) : (
                      <span
                        onClick={(e) => { e.stopPropagation(); startEditAccount(c.chat_id, c.account_id || ''); }}
                        style={{ cursor: 'pointer', borderBottom: '1px dashed #aaa' }}
                        title="Нажмите, чтобы изменить номер счёта"
                      >
                        {c.account_id ? `Л/с: ${c.account_id}` : '+ добавить номер счёта'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Правая панель — диалог */}
        <div style={{ flex: 1, background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
          {activeChatId ? (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {messages.filter(m => m != null).map((msg, idx, arr) => {
                  const mid = msg.body?.mid;
                  const canEdit = !!mid && operatorMids.has(mid);
                  const attachments = msg.body?.attachments || [];
                  const ts = msg.timestamp || Date.now();
                  const prevTs = idx > 0 ? (arr[idx - 1].timestamp || 0) : 0;
                  const showDaySeparator = idx === 0 || (prevTs && !isSameDay(prevTs, ts));

                  return (
                    <React.Fragment key={mid || idx}>
                      {showDaySeparator && (
                        <div style={{
                          textAlign: 'center',
                          margin: '16px 0 10px',
                          fontSize: 12,
                          color: '#888',
                        }}>
                          <span style={{
                            background: '#f0f2f5',
                            padding: '4px 12px',
                            borderRadius: 12,
                          }}>
                            {formatDayLabel(ts)}
                          </span>
                        </div>
                      )}
                      <div style={{ marginBottom: 10, textAlign: msg.sender?.is_bot ? 'right' : 'left' }}>
                        <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>
                          {msg.sender?.first_name || (msg.sender?.is_bot ? 'Оператор' : 'Пользователь')} · {formatTime(ts)}
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 6, maxWidth: '85%' }}>
                          {canEdit && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <button onClick={() => startEdit(mid!, msg.body?.text || '')} title="Редактировать"
                                style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                              <button onClick={() => deleteMessage(mid!)} title="Удалить"
                                style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '2px 6px', cursor: 'pointer', fontSize: 12, color: '#cf1322' }}>🗑️</button>
                            </div>
                          )}
                          <div style={{
                            display: 'inline-block', padding: '8px 14px', borderRadius: 18,
                            background: msg.sender?.is_bot ? '#dcf8c6' : '#fff',
                            border: '1px solid #ddd',
                            textAlign: 'left',
                            wordBreak: 'break-word',
                          }}>
                            {msg.body?.text}
                            {attachments.map((att: any, i: number) => (
                              <div key={i} style={{ marginTop: 4 }}>
                                {att.type === 'image' && (att.payload?.url
                                  ? <img src={att.payload.url} style={{ maxWidth: 200, borderRadius: 8 }} alt="attachment" />
                                  : <span style={{ color: '#888' }}>[изображение]</span>)}
                                {att.type === 'audio' && <span style={{ color: '#555' }}>🎤 Голосовое сообщение (переслано в личку)</span>}
                                {att.type === 'video' && <span style={{ color: '#555' }}>📹 Видео-сообщение (переслано в личку)</span>}
                                {!['image','audio','video'].includes(att.type) && <span style={{ color: '#888' }}>📎 Файл ({att.type})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Панель шаблонов */}
              {showTemplates && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0', maxHeight: 160, overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#888' }}>Быстрые ответы</span>
                    <button onClick={() => setShowTemplates(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>✕</button>
                  </div>
                  {templates.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#aaa', padding: 8 }}>Шаблонов пока нет. Добавьте их в Настройках.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {templates.map(t => (
                        <button key={t.id} onClick={() => insertTemplate(t.text)}
                          style={{
                            textAlign: 'left', padding: '6px 10px', borderRadius: 6,
                            background: '#fafafa', border: '1px solid #f0f0f0',
                            cursor: 'pointer', fontSize: 13
                          }}>
                          <strong style={{ marginRight: 6 }}>{t.title}</strong>
                          <span style={{ color: '#888' }}>{t.text.length > 60 ? t.text.slice(0, 60) + '…' : t.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Панель ввода */}
              <div style={{ display: 'flex', marginTop: 10, alignItems: 'flex-end', gap: 8 }}>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}
                  title="Быстрые ответы"
                >
                  📝
                </button>
                <button
                  onClick={() => setShowImageInput(!showImageInput)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}
                  title="Прикрепить изображение по URL"
                >
                  🖼️
                </button>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {showImageInput && (
                    <input
                      type="text"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrl}
                      onChange={e => setImageUrl(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9' }}
                    />
                  )}
                  {editingMid && (
                    <div style={{ fontSize: 12, color: '#d46b08', display: 'flex', justifyContent: 'space-between' }}>
                      <span>✏️ Редактирование сообщения</span>
                      <button onClick={cancelEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>Отменить</button>
                    </div>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Введите сообщение… (Enter — отправить, Shift+Enter — новая строка)"
                    rows={1}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 18,
                      border: '1px solid #d9d9d9',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      lineHeight: 1.4,
                      boxSizing: 'border-box',
                      maxHeight: 150,
                      overflowY: 'auto',
                    }}
                  />
                </div>
                <button onClick={sendMessage} style={{ background: '#1677ff', color: 'white', border: 'none', borderRadius: 24, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {editingMid ? 'Сохранить' : 'Отправить'}
                </button>
                <button onClick={handleCloseChat} style={{ background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322', borderRadius: 24, padding: '10px 18px', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>🔚 Завершить</button>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
              Выберите чат слева, чтобы начать диалог
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveChat;